import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { ApiService } from "@core/services";
import { ApiResponse, DataListItem, RegistryOwnerStateItem } from "@core/types";

const DATALIST_API = {
  //Alphabetical order
  ALERT_GROUPS: "api/data-list/list-alert-group",
  APPROACHER_PEOPLE: "api/data-list/list-approacher-person",
  CAUSE_OF_DEATH: "api/data-list/list-cause-of-death",
  CONTROLS: "api/data-list/report-parameter-controls",
  COORDINATORS: "api/data-list/list-report-coordinator",
  DATE_TYPES: "api/data-list/list-date-type",
  EVENT_MAIN_CATEGORIES: "api/data-list/list-event-main-category",
  EVENT_SUB_CATEGORIES: "api/data-list/list-event-sub-category",
  INVOICE_DATES: "api/data-list/list-invoice-date",
  MESSAGE_IMPORT_ORGS: "api/data-list/list-message-import-org",
  MONTHS: "api/data-list/list-month",
  ORGANIZATIONS: "api/data-list/list-report-organization",
  ORGANIZATIONS_BY_STATE_AND_TYPE: "api/data-list/list-organization-by-state-and-type",
  ORGANIZATION_TYPES: "api/data-list/list-organization-type",
  PENDING_REFERRAL_TYPES: "api/data-list/list-pending-referral-type",
  PEOPLE_BY_ORG: "api/data-list/list-person-by-org",
  QA_TRACKING_TYPES: "api/data-list/list-qa-tracking-type",
  REFERRAL_TYPES: "api/data-list/list-referral-type",
  REGISTRY_OWNER_STATES: "api/data-list/registery-owner-states",
  REPORT_DEFAULTS: "api/data-list/report-parameter-defaults",
  REPORT_GROUPS: "api/data-list/list-report-group",
  SCHEDULE_GROUPS: "api/data-list/list-schedule-group",
  SCHEDULE_ORGANIZATIONS: "api/data-list/list-schedule-organization",
  SCHEDULE_PEOPLE: "api/data-list/list-schedule-people",
  SCREENING_CRITERIA_GROUPS: "api/data-list/list-screening-criteria-groups",
  SCREENING_CRITERIA_ORGANIZATIONS: "api/data-list/list-screening-criteria-organization",
  SORT_TYPES: "api/data-list/list-report-sort-type",
  SOURCE_CODES: "api/data-list/list-source-code",
  STATES: "api/data-list/list-state",
  STATTRAC_USERS: "api/data-list/list-stattrac-user",
  YEARS: "api/data-list/list-year",
};

@Injectable({ providedIn: "root" })
export class DataListService {
  private readonly api = inject(ApiService);
  //----------------------------------------------------
  //  Data List Endpoints for Dropdowns
  //----------------------------------------------------
  getDateTypes(reportId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.DATE_TYPES, { params: { reportID: reportId } });
  }

  getReportGroups(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.REPORT_GROUPS);
  }

  getOrganizations(reportGroupId: number, lastRowId: number, searchQuery: string): Observable<ApiResponse<DataListItem[]>> {
    const params = {
      reportGroupId,
      lastRowId,
      searchQuery: searchQuery,
    };

    return this.api.get<DataListItem[]>(DATALIST_API.ORGANIZATIONS, { params });
  }

  getSourceCodes(reportGroupId: number, typeId?: number): Observable<ApiResponse<DataListItem[]>> {
    const params: Record<string, string | number> = { webReportGroupId: reportGroupId };
    if (typeId) params.sourceCodeTypeId = typeId;

    return this.api.get<DataListItem[]>(DATALIST_API.SOURCE_CODES, { params });
  }

  getCoordinators(reportGroupId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.COORDINATORS, { params: { webreportGroupId: reportGroupId } });
  }

  getSortTypeOptions(reportId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.SORT_TYPES, { params: { reportId } });
  }

  getReferralTypes(reportGroupId: number, sourceCodeId?: number | null): Observable<ApiResponse<DataListItem[]>> {
    const params: Record<string, string | number> = {  reportGroupId };
    if (sourceCodeId) params.sourceCodeId = sourceCodeId;

    return this.api.get<DataListItem[]>(DATALIST_API.REFERRAL_TYPES, { params });
  }

  getPendingReferralTypes(reportGroupId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.PENDING_REFERRAL_TYPES, { params: { reportGroupId } });
  }

  getCauseOfDeathList(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.CAUSE_OF_DEATH);
  }

  getMonthList(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.MONTHS);
  }

  getYearList(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.YEARS);
  }

  getOrganizationTypeList(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.ORGANIZATION_TYPES);
  }

  getQATrackingTypeList(organizationId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.QA_TRACKING_TYPES, { params: { organizationId } });
  }

  getAlertGroupList(alertTypeId?: number | null, sourceCodeName?: string | null): Observable<ApiResponse<DataListItem[]>> {
    const params: Record<string, string | number> = {};
    if (alertTypeId) params.alertTypeId = alertTypeId;
    if (sourceCodeName) params.sourceCodeName = sourceCodeName;

    return this.api.get<DataListItem[]>(DATALIST_API.ALERT_GROUPS, { params });
  }

  getMessageImportOrganizationsList(sourceCodeName?: string | null, sourceCodeTypeId?: number | null, organizationId?: number | null): Observable<ApiResponse<DataListItem[]>> {
    const params: Record<string, string | number> = {};
    if (sourceCodeName) params.sourceCodeName = sourceCodeName;
    if (sourceCodeTypeId) params.sourceCodeTypeId = sourceCodeTypeId;
    if (organizationId) params.organizationId = organizationId;

    return this.api.get<DataListItem[]>(DATALIST_API.MESSAGE_IMPORT_ORGS, { params });
  }

  getPeopleByOrganizationList(organizationId: number, inactive: number): Observable<ApiResponse<DataListItem[]>> {
    const params: Record<string, string | number> = {
      organizationId,
      inactive,
    };
    
    return this.api.get<DataListItem[]>(DATALIST_API.PEOPLE_BY_ORG, { params });
  }

  getStattracUserList(reportGroupId?: number | null): Observable<ApiResponse<DataListItem[]>> {
    const params: Record<string, string | number> = {};
    if (reportGroupId) params.reportGroupId = reportGroupId;

    return this.api.get<DataListItem[]>(DATALIST_API.STATTRAC_USERS, { params });
  }

  getScheduleOrganizationList(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.SCHEDULE_ORGANIZATIONS);
  }

  getScheduleGroupList(organizationId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.SCHEDULE_GROUPS, { params: { organizationId } });
  }

  getScreeningCriteriaOrganizationList(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.SCREENING_CRITERIA_ORGANIZATIONS);
  }

  getScreeningCriteriaGroupList(organizationId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.SCREENING_CRITERIA_GROUPS, { params: { organizationId } });
  }

  getApproacherPersonList(organizationId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.APPROACHER_PEOPLE, { params: { organizationId } });
  }

  getStateList(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.STATES);
  }

  getInvoiceDateList(): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.INVOICE_DATES);
  }

  getOrganizationByStateAndTypeList(stateId?: number  | null, organizationTypeId?: number | null): Observable<ApiResponse<DataListItem[]>> {
    const params: Record<string, string | number> = {};
    if (stateId) params.stateId = stateId;
    if (organizationTypeId) params.organizationTypeId = organizationTypeId;

    return this.api.get<DataListItem[]>(DATALIST_API.ORGANIZATIONS_BY_STATE_AND_TYPE, { params });
  }

  getEventMainCategoryList(state: string): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.EVENT_MAIN_CATEGORIES, { params: { state } });
  }

  getEventSubCategoryList(eventCategoryId: number, state: string): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.EVENT_SUB_CATEGORIES, { params: { eventCategoryId, state } });
  }

  getRegistryOwnerStates(displayAllStates: boolean): Observable<ApiResponse<RegistryOwnerStateItem[]>> {
    return this.api.get<RegistryOwnerStateItem[]>(DATALIST_API.REGISTRY_OWNER_STATES, { params: { displayAllStates } });
  }

  getSchedulePeople(organizationId: number, scheduleGroupId: number): Observable<ApiResponse<DataListItem[]>> {
    return this.api.get<DataListItem[]>(DATALIST_API.SCHEDULE_PEOPLE, { params: { organizationId, scheduleGroupId } });
  }
}
